import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError, showRechargeSuccess } from '../utils/notifications';
import { exportCAListToExcel } from '../utils/excelExport';

function CAList() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [caUsers, setCaUsers] = useState([]);
  const [activeSection, setActiveSection] = useState('ca-list'); // Define a new active section ID
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Estado para el filtro de búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCaUsers, setFilteredCaUsers] = useState([]);

  // Estado para vista activos/ocultos
  const [viewTab, setViewTab] = useState('activos');
  const [hidingCA, setHidingCA] = useState(null);

  // Estado para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const caUsersPerPage = 15; // 15 usuarios por página


  // Estado para el reset de dispositivo
  const [resettingDevice, setResettingDevice] = useState(null);

  // Estado para la exportación a Excel
  const [exportingExcel, setExportingExcel] = useState(false);

  // Calcular usuarios para la página actual
  const indexOfLastCaUser = currentPage * caUsersPerPage;
  const indexOfFirstCaUser = indexOfLastCaUser - caUsersPerPage;
  const currentCaUsers = filteredCaUsers.slice(indexOfFirstCaUser, indexOfLastCaUser);
  const totalPages = Math.ceil(filteredCaUsers.length / caUsersPerPage);

  // Función para cambiar de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Función para ir a la página anterior
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Función para ir a la página siguiente
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };


  // Función para resetear dispositivo (eliminar campo idDispositivo)
  const handleResetDevice = async (caUser) => {
    if (resettingDevice === caUser.id) return;

    setResettingDevice(caUser.id);
    try {
      console.log(`🔄 Reseteando dispositivo de CA: ${caUser.nombre}`);

      // Actualizar el documento eliminando completamente el campo idDispositivo
      const caDocRef = doc(db, 'usuarios', caUser.id);
      await updateDoc(caDocRef, {
        idDispositivo: deleteField() // Solo eliminar el campo, sin actualizar updatedAt
      });

      console.log(`✅ Campo idDispositivo eliminado completamente de ${caUser.nombre}`);

      // Actualizar la lista local
      const updatedCaUsers = caUsers.map(ca => 
        ca.id === caUser.id 
          ? { ...ca, idDispositivo: 'N/A' }
          : ca
      );
      setCaUsers(updatedCaUsers);
      
      // Recalcular lista filtrada con el término de búsqueda actual
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filteredUpdated = updatedCaUsers.filter(user => 
        user.nombre.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.numberCa.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.serial.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.cedula.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.direccion.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.idDispositivo.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.email.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.tipoVehiculos.toLowerCase().includes(lowerCaseSearchTerm)
      );
      setFilteredCaUsers(filteredUpdated);

      showRechargeSuccess(
        "Dispositivo Reseteado",
        `El campo idDispositivo del CA ${caUser.nombre} (${caUser.numberCa}) ha sido eliminado completamente de la base de datos.`
      );

    } catch (error) {
      console.error("Error al resetear dispositivo:", error);
      showError("Error", `No se pudo resetear el dispositivo: ${error.message}`);
    } finally {
      setResettingDevice(null);
    }
  };

  // Función para ocultar un CA (no elimina, solo marca oculto:true)
  const handleHideCA = async (caUser) => {
    if (hidingCA === caUser.id) return;
    setHidingCA(caUser.id);
    try {
      await updateDoc(doc(db, 'usuarios', caUser.id), { oculto: true });
      setCaUsers(prev => prev.map(ca => ca.id === caUser.id ? { ...ca, oculto: true } : ca));
      showRechargeSuccess('CA Ocultado', `${caUser.nombre} (${caUser.numberCa}) fue movido a la vista Ocultos.`);
    } catch (error) {
      showError('Error', `No se pudo ocultar el CA: ${error.message}`);
    } finally {
      setHidingCA(null);
    }
  };

  // Función para restaurar un CA oculto
  const handleRestoreCA = async (caUser) => {
    if (hidingCA === caUser.id) return;
    setHidingCA(caUser.id);
    try {
      await updateDoc(doc(db, 'usuarios', caUser.id), { oculto: false });
      setCaUsers(prev => prev.map(ca => ca.id === caUser.id ? { ...ca, oculto: false } : ca));
      showRechargeSuccess('CA Restaurado', `${caUser.nombre} (${caUser.numberCa}) volvió a la lista principal.`);
    } catch (error) {
      showError('Error', `No se pudo restaurar el CA: ${error.message}`);
    } finally {
      setHidingCA(null);
    }
  };

  // Función para exportar lista de CAs a Excel
  const handleExportToExcel = async () => {
    if (exportingExcel) return;

    setExportingExcel(true);
    try {
      console.log('📊 Exportando lista de CAs a Excel...');
      
      // Usar la lista filtrada para la exportación
      const result = exportCAListToExcel(filteredCaUsers, userRole, searchTerm);
      
      if (result.success) {
        showRechargeSuccess(
          "Exportación Exitosa",
          `Lista de CAs exportada como: ${result.filename}`
        );
      } else {
        showError("Error de Exportación", result.message);
      }

    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      showError("Error", `No se pudo exportar: ${error.message}`);
    } finally {
      setExportingExcel(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Fetch user role
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          const userData = userDoc.data();
          setUser(currentUser);
          setUserRole(userData.rol || 'Usuario');

          // Check if user has permission (Admin, Auditor or Recaudador)
          if (userData.rol !== 'Admin' && userData.rol !== 'Auditor' && userData.rol !== 'Recaudador') {
            showError("Acceso denegado", "Solo administradores, auditores y recaudadores pueden ver la lista de CAs");
            navigate('/dashboard');
            return;
          }

          // Fetch CA users
          const caQuery = query(collection(db, 'usuarios'), where('rol', '==', 'CA'));
          const querySnapshot = await getDocs(caQuery);
          const usersList = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            usersList.push({
              id: doc.id,
              nombre: data.nombre || 'N/A',
              email: data.email || doc.id,
              numberCa: data.numberCa || 'N/A',
              serial: data.serial || 'N/A',
              cedula: data.cedula || 'N/A',
              direccion: data.direccion || 'N/A',
              idDispositivo: data.idDispositivo || 'N/A',
              balance: data.balance || 0,
              status: data.status === 1 ? 'Activo' : 'Inactivo',
              zona: data.zona || 'N/A',
              tipoVehiculos: data.tipoVehiculos || 'autos_y_motos',
              oculto: data.oculto === true,
            });
          });
          setCaUsers(usersList);
          // Inicialmente, mostrar todos los usuarios
          setFilteredCaUsers(usersList);

        } catch (error) {
          console.error("Error fetching CA users or verifying role:", error);
          showError("Error", "No se pudieron cargar los usuarios CA o verificar tu rol");
        } finally {
          setLoading(false);
        }
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Effect to filter users based on search term and active tab
  useEffect(() => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const baseList = viewTab === 'ocultos'
      ? caUsers.filter(u => u.oculto)
      : caUsers.filter(u => !u.oculto);
    const filtered = baseList.filter(user =>
      user.nombre.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.numberCa.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.serial.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.cedula.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.direccion.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.idDispositivo.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.email.toLowerCase().includes(lowerCaseSearchTerm) ||
      user.tipoVehiculos.toLowerCase().includes(lowerCaseSearchTerm)
    );
    setFilteredCaUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, caUsers, viewTab]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Lista de CAs" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
      
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">Lista de Controladores Autorizados</h1>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">{userRole || "Usuario"}</p>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">
            {/* Search Input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Buscar por Nombre, CA #, Serial, Cédula, Dirección, ID Dispositivo, Email o Tipo de Vehículos"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Tabs activos / ocultos */}
            <div className="flex space-x-1 mb-4">
              <button
                onClick={() => setViewTab('activos')}
                className={`px-4 py-2 rounded-t-lg text-sm font-medium border-b-2 transition-colors ${
                  viewTab === 'activos'
                    ? 'border-blue-500 text-blue-600 bg-white shadow-sm'
                    : 'border-transparent text-gray-500 hover:text-gray-700 bg-gray-100'
                }`}
              >
                Activos ({caUsers.filter(u => !u.oculto).length})
              </button>
              <button
                onClick={() => setViewTab('ocultos')}
                className={`px-4 py-2 rounded-t-lg text-sm font-medium border-b-2 transition-colors ${
                  viewTab === 'ocultos'
                    ? 'border-gray-500 text-gray-700 bg-white shadow-sm'
                    : 'border-transparent text-gray-400 hover:text-gray-600 bg-gray-100'
                }`}
              >
                Ocultos ({caUsers.filter(u => u.oculto).length})
              </button>
            </div>

            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {viewTab === 'ocultos' ? 'CAs Ocultos' : 'Controladores Autorizados'} ({filteredCaUsers.length})
                </h2>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                  <button
                    onClick={handleExportToExcel}
                    disabled={exportingExcel || filteredCaUsers.length === 0}
                    className={`${
                      exportingExcel 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-green-500 hover:bg-green-600'
                    } text-white px-4 py-2 rounded text-sm font-medium flex items-center justify-center space-x-2 w-full sm:w-auto`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">
                      {exportingExcel ? 'Exportando...' : `📊 Descargar Excel (${filteredCaUsers.length})`}
                    </span>
                    <span className="sm:hidden">
                      {exportingExcel ? 'Exportando...' : `📊 Excel (${filteredCaUsers.length})`}
                    </span>
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        console.log('🔍 Verificando dirección del CA51...');
                        
                        // Buscar CA51 en la lista actual
                        const ca51 = caUsers.find(ca => 
                          ca.numberCa === 'CA51' || 
                          (ca.nombre === 'Gabriel Zambrano' && ca.cedula === '1720860889')
                        );
                        
                        if (ca51) {
                          if (ca51.direccion && ca51.direccion.trim() !== '') {
                            showRechargeSuccess(
                              "Dirección encontrada", 
                              `CA51 - Gabriel Zambrano ya tiene dirección: ${ca51.direccion}`
                            );
                          } else {
                            // Actualizar dirección directamente en Firebase
                            const direccionEjemplo = 'Av. Principal, Sector Centro, Quito';
                            const caDocRef = doc(db, 'usuarios', ca51.id);
                            await updateDoc(caDocRef, {
                              direccion: direccionEjemplo,
                              updatedAt: new Date().toISOString()
                            });
                            
                            // Actualizar la lista local
                            setCaUsers(prev => prev.map(ca => 
                              ca.id === ca51.id ? { ...ca, direccion: direccionEjemplo } : ca
                            ));
                            
                            showRechargeSuccess(
                              "Dirección agregada", 
                              `CA51 - Gabriel Zambrano: ${direccionEjemplo}`
                            );
                          }
                        } else {
                          showError("CA no encontrado", "No se encontró CA51 - Gabriel Zambrano en la lista");
                        }
                      } catch (error) {
                        console.error('Error:', error);
                        showError("Error", `No se pudo verificar/actualizar: ${error.message}`);
                      }
                    }}
                    className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded text-sm w-full sm:w-auto text-center"
                  >
                    🔍 <span className="hidden sm:inline">Verificar </span>CA51
                  </button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CA #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cédula</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dirección</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zona</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo de Vehículos</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      {userRole !== 'Auditor' && (
                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentCaUsers.map((caUser) => (
                      <tr key={caUser.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{caUser.nombre}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caUser.numberCa}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{caUser.serial}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caUser.cedula}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={caUser.direccion}>{caUser.direccion}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caUser.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caUser.zona}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {caUser.tipoVehiculos === 'autos_y_motos' ? '🚗🏍️ Autos y Motos' :
                           caUser.tipoVehiculos === 'solo_autos' ? '🚗 Solo Autos' :
                           caUser.tipoVehiculos === 'solo_motos' ? '🏍️ Solo Motos' : 
                           caUser.tipoVehiculos}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${caUser.status === 'Activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {caUser.status}
                          </span>
                        </td>
                        {userRole !== 'Auditor' && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex flex-col space-y-2">
                              <Link to={`/edit-ca/${caUser.id}`} className="text-blue-600 hover:text-blue-900">
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editar
                              </Link>
                              <button
                                onClick={() => handleResetDevice(caUser)}
                                disabled={resettingDevice === caUser.id}
                                className={`text-orange-600 hover:text-orange-900 ${resettingDevice === caUser.id ? 'opacity-50' : ''}`}
                                title="Resetear Dispositivo"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {resettingDevice === caUser.id ? 'Reseteando...' : 'Reset Dispositivo'}
                              </button>
                              {userRole === 'Admin' && (
                                viewTab === 'ocultos' ? (
                                  <button
                                    onClick={() => handleRestoreCA(caUser)}
                                    disabled={hidingCA === caUser.id}
                                    className={`text-green-600 hover:text-green-800 ${hidingCA === caUser.id ? 'opacity-50' : ''}`}
                                    title="Restaurar CA a la lista principal"
                                  >
                                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>
                                    {hidingCA === caUser.id ? 'Restaurando...' : 'Restaurar'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleHideCA(caUser)}
                                    disabled={hidingCA === caUser.id || caUser.status === 'Activo'}
                                    className={`text-yellow-600 hover:text-yellow-800 ${(hidingCA === caUser.id || caUser.status === 'Activo') ? 'opacity-40 cursor-not-allowed' : ''}`}
                                    title={caUser.status === 'Activo' ? 'Solo se pueden ocultar CAs inactivos' : 'Ocultar CA (no se elimina)'}
                                  >
                                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    </svg>
                                    {hidingCA === caUser.id ? 'Ocultando...' : 'Ocultar'}
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {currentCaUsers.length === 0 && filteredCaUsers.length > 0 && (
                      <tr>
                        <td colSpan={userRole !== 'Auditor' ? 10 : 9} className="px-6 py-4 text-center text-gray-500">
                          No se encontraron usuarios CA en esta página.
                        </td>
                      </tr>
                    )}
                     {filteredCaUsers.length === 0 && searchTerm && !loading && (
                      <tr>
                        <td colSpan={userRole !== 'Auditor' ? 10 : 9} className="px-6 py-4 text-center text-gray-500">
                          No se encontraron usuarios CA que coincidan con "{searchTerm}".
                        </td>
                      </tr>
                    )}
                     {filteredCaUsers.length === 0 && !searchTerm && !loading && caUsers.length === 0 && (
                      <tr>
                        <td colSpan={userRole !== 'Auditor' ? 10 : 9} className="px-6 py-4 text-center text-gray-500">
                          No hay usuarios CA disponibles.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {filteredCaUsers.length > caUsersPerPage && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Anterior
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Siguiente
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{indexOfFirstCaUser + 1}</span> a{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastCaUser, filteredCaUsers.length)}
                        </span>{' '}
                        de <span className="font-medium">{filteredCaUsers.length}</span> usuarios
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                          <span className="sr-only">Anterior</span>
                          &larr;
                        </button>
                        {/* Page Numbers (simplified) */}
                        {/* You might want to add logic here to show a limited number of page buttons */}
                        {Array.from({ length: totalPages }).map((_, idx) => (
                          <button
                            key={idx + 1}
                            onClick={() => handlePageChange(idx + 1)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === idx + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                          >
                            {idx + 1}
                          </button>
                        ))}
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                          <span className="sr-only">Siguiente</span>
                          &rarr;
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

    </div>
  );
}

export default CAList; 